import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ConfirmDialog, EmptyState, LoadingState, PageHeader, SectionCard, StatusChip, useToast } from '../../../ui';
import { getAdminToken, startDownload } from '../../OrgLicensing/licApi';
import { formatDate, formatSize, noteLines } from '../../OrgLicensing/orgFormat';
import { tableContainerSx } from '../../OrgLicensing/OrgCommon';

const API = `${process.env.REACT_APP_API_BASE_URL}/api/admin/installers`;
const IN_PROGRESS = ['Queued', 'Sending'];

class AdminApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

/** An expired admin session: forget the token (or the login page would bounce straight back) and sign in again. */
const signInAgain = () => {
  localStorage.removeItem('jwttoken');
  sessionStorage.removeItem('jwttoken');
  window.location.href = '/superadmin';
};

const readJson = async (response) => {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch (error) {
    return { message: text };
  }
};

/** Calls the installers admin API with the Vavetek admin JWT. A 401 goes back to the super admin login. */
const adminFetch = async (path, { method = 'GET', body } = {}) => {
  const response = await fetch(`${API}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${getAdminToken()}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await readJson(response);
  if (response.status === 401) {
    signInAgain();
    throw new AdminApiError('Your session has ended. Please sign in again.', 401);
  }
  if (!response.ok || json.success === false) {
    throw new AdminApiError(json.message || `Request failed (${response.status}).`, response.status);
  }
  return json;
};

const slotPath = (slot) => `/${slot.product}/${slot.edition}`;

/** PUTs the file straight to storage (R2) at the signed URL from the API, with progress. */
const putToStorage = (upload, file, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(upload.method || 'PUT', upload.url);
    Object.entries(upload.headers || {}).forEach(([name, value]) => xhr.setRequestHeader(name, value));
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new AdminApiError(`The upload to storage failed (${xhr.status}). Try again.`, xhr.status));
    xhr.onerror = () =>
      reject(new AdminApiError('The upload to storage failed. Check your connection (and the bucket CORS settings) and try again.', 0));
    xhr.send(file);
  });

/**
 * Publishes a release: claims the version (the API returns a signed upload URL), uploads the file straight to R2, then
 * confirms it. A failed upload is cancelled so the version can be tried again.
 */
const publishRelease = async (slot, { file, version, releaseNotes, notify }, onProgress) => {
  const { release, upload } = await adminFetch(`${slotPath(slot)}/uploads`, {
    method: 'POST',
    body: { version, releaseNotes, fileName: file.name, sizeBytes: file.size },
  });
  try {
    await putToStorage(upload, file, onProgress);
    const result = await adminFetch(`/releases/${encodeURIComponent(release.id)}/complete`, { method: 'POST', body: { notify } });
    return result.release;
  } catch (error) {
    // Wait for the cancel, so a retry of the same version doesn't hit the old claim (409 VersionExists).
    await adminFetch(`/releases/${encodeURIComponent(release.id)}/abort`, { method: 'POST' }).catch(() => {});
    throw error;
  }
};

const EmailStatus = ({ release }) => {
  switch (release.emailStatus) {
    case 'Queued':
      return <StatusChip label="Queued" tone="info" />;
    case 'Sending':
      return <StatusChip label={`Sending ${release.emailSent}/${release.emailRecipients}`} tone="info" />;
    case 'Sent':
      return (
        <Tooltip title={`${release.emailOrgs} licenses · ${formatDate(release.emailedAtUtc)}${release.emailFailed ? ` · ${release.emailFailed} failed` : ''}`}>
          <span>
            <StatusChip label={`Sent to ${release.emailSent}`} tone={release.emailFailed ? 'warning' : 'success'} />
          </span>
        </Tooltip>
      );
    case 'Interrupted':
      return (
        <Tooltip title="The server restarted while sending. Some admins may not have the email; you can send it again.">
          <span>
            <StatusChip label={`Interrupted · ${release.emailSent}/${release.emailRecipients}`} tone="warning" />
          </span>
        </Tooltip>
      );
    case 'Failed':
      return <StatusChip label="Failed" tone="error" />;
    default:
      return <StatusChip label="Not sent" tone="neutral" />;
  }
};

/** Dialog to publish a new version of one product edition: file, version, notes, optional email with preview and test. */
const PublishDialog = ({ slot, open, onClose, onPublished }) => {
  const toast = useToast();
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [notify, setNotify] = useState(true);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [testing, setTesting] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    if (open) {
      setFile(null);
      setVersion('');
      setNotes('');
      setNotify(true);
      setProgress(null);
      setError('');
      setPreview(null);
    }
  }, [open]);

  // Live email preview, refreshed shortly after typing stops.
  useEffect(() => {
    if (!open || !slot) return undefined;
    const timer = setTimeout(async () => {
      try {
        const result = await adminFetch(`${slotPath(slot)}/email-preview`, {
          method: 'POST',
          body: { version, releaseNotes: notes, sizeBytes: file?.size || 0 },
        });
        setPreview(result);
      } catch (previewError) {
        setPreview(null);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [open, slot, version, notes, file]);

  const uploading = progress !== null;
  const valid = file && version.trim() && notes.trim();

  const handleTest = async () => {
    setTesting(true);
    try {
      const result = await adminFetch(`${slotPath(slot)}/test-email`, {
        method: 'POST',
        body: { version, releaseNotes: notes, sizeBytes: file?.size || 0 },
      });
      toast(`Test email sent to ${result.sentTo}.`);
    } catch (testError) {
      toast(testError.message, 'error');
    } finally {
      setTesting(false);
    }
  };

  const handlePublish = async () => {
    setError('');
    setProgress(0);
    try {
      const release = await publishRelease(
        slot,
        { file, version: version.trim(), releaseNotes: notes.trim(), notify },
        setProgress
      );
      onPublished(release, notify);
    } catch (publishError) {
      setError(publishError.message);
      setProgress(null);
    }
  };

  if (!slot) return null;

  return (
    <Dialog open={open} onClose={uploading ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        Publish a new {slot.name} {slot.edition} version
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, pt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Box>
              <input
                ref={fileInput}
                type="file"
                accept=".exe,.msi,.zip"
                hidden
                onChange={(event) => setFile(event.target.files?.[0] || null)}
              />
              <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => fileInput.current?.click()} disabled={uploading}>
                {file ? 'Choose a different file' : 'Choose installer file'}
              </Button>
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', wordBreak: 'break-all' }}>
                {file ? `${file.name} · ${formatSize(file.size)}` : '.exe, .msi or .zip, up to 5 GB'}
              </Typography>
            </Box>
            <TextField
              label="Version"
              placeholder="2.4.0"
              value={version}
              onChange={(event) => setVersion(event.target.value)}
              disabled={uploading}
              required
            />
            <TextField
              label="Release notes"
              placeholder={'Faster clash grouping on large models\nFixed license check after the PC wakes from sleep'}
              helperText="One change per line. The email shows the first 6 lines."
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              disabled={uploading}
              multiline
              minRows={5}
              required
            />
            <FormControlLabel
              control={<Checkbox checked={notify} onChange={(event) => setNotify(event.target.checked)} disabled={uploading} />}
              label={`Email the IT admins and holders of all ${slot.edition} licenses`}
            />
            {slot.publicUpdates && (
              <Alert severity="info">
                The online {slot.name} plugin checks this slot for updates, so its users are offered this version automatically.
              </Alert>
            )}
            {uploading && (
              <Box>
                <LinearProgress variant="determinate" value={progress} />
                <Typography variant="caption">
                  {progress < 100 ? `Uploading to storage… ${progress}%` : 'Publishing…'}
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2">Email preview</Typography>
              <Button size="small" startIcon={testing ? <CircularProgress size={14} /> : <MailOutlineIcon />} onClick={handleTest} disabled={testing}>
                Send test email to me
              </Button>
            </Box>
            <Typography variant="caption">
              {preview ? `Subject: ${preview.subject}` : 'Type a version and notes to see the email.'}
            </Typography>
            <Box
              component="iframe"
              title="Email preview"
              srcDoc={preview?.html || ''}
              sandbox=""
              sx={{ width: '100%', height: 480, border: '1px solid', borderColor: 'divider', borderRadius: '10px', bgcolor: '#F8F6F1' }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handlePublish} disabled={!valid || uploading}>
          {uploading ? <CircularProgress size={20} color="inherit" /> : notify ? 'Publish and email' : 'Publish'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Version history of one product edition, with its email status and actions. */
const ReleaseTable = ({ releases, busyId, onEmail, onDownload }) => (
  <Box sx={{ ...tableContainerSx, mb: 0 }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Version</TableCell>
          <TableCell>Released</TableCell>
          <TableCell>Size</TableCell>
          <TableCell>Release notes</TableCell>
          <TableCell>Email</TableCell>
          <TableCell align="right">Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {releases.map((release, index) => (
          <TableRow key={release.id} hover>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {release.version}
                {index === 0 && <StatusChip label="Latest" tone="brand" />}
              </Box>
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(release.publishedAtUtc)}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatSize(release.sizeBytes)}</TableCell>
            <TableCell sx={{ maxWidth: 320 }}>
              <Typography variant="body2" noWrap title={release.releaseNotes}>
                {noteLines(release.releaseNotes)[0]}
              </Typography>
            </TableCell>
            <TableCell>
              <EmailStatus release={release} />
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
              <Button
                size="small"
                disabled={busyId === release.id || IN_PROGRESS.includes(release.emailStatus)}
                onClick={() => onEmail(release)}
              >
                {['Sent', 'Interrupted'].includes(release.emailStatus) ? 'Email again' : 'Send email'}
              </Button>
              <Tooltip title="Download">
                <IconButton size="small" onClick={() => onDownload(release)} aria-label={`Download ${release.version}`}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

/**
 * Vavetek admin: publish installer versions in six slots (BAMROC, HUBAROC, CRDR × Online, Offline), see the history,
 * and email the IT admins of licenses of the same edition.
 */
const InstallersView = () => {
  const toast = useToast();
  const [products, setProducts] = useState(null);
  const [error, setError] = useState('');
  const [publishing, setPublishing] = useState(null); // { product, name, edition, publicUpdates }
  const [resend, setResend] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const result = await adminFetch('');
      setProducts(result.products);
      setError('');
    } catch (loadError) {
      if (loadError.status !== 401) setError(loadError.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // While emails are going out, refresh so the progress stays current.
  const sending = (products || []).some((p) => p.editions.some((e) => e.releases.some((r) => IN_PROGRESS.includes(r.emailStatus))));
  useEffect(() => {
    if (!sending) return undefined;
    const timer = setInterval(load, 4000);
    return () => clearInterval(timer);
  }, [sending, load]);

  const handlePublished = (release, emailed) => {
    setPublishing(null);
    toast(`${release.name} ${release.edition} ${release.version} published${emailed ? `. Emails to ${release.edition} license admins are on their way.` : '.'}`);
    load();
  };

  const sendEmails = async (release, resend = false) => {
    setBusyId(release.id);
    try {
      await adminFetch(`/releases/${encodeURIComponent(release.id)}/notify`, { method: 'POST', body: { resend } });
      toast(`Emailing ${release.edition} license admins about ${release.name} ${release.version}.`);
      setResend(null);
      load();
    } catch (sendError) {
      if (sendError.status !== 401) toast(sendError.message, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const download = async (release) => {
    try {
      const { path } = await adminFetch(`/releases/${encodeURIComponent(release.id)}/link`, { method: 'POST' });
      startDownload(path);
    } catch (downloadError) {
      if (downloadError.status !== 401) toast(downloadError.message, 'error');
    }
  };

  return (
    <Box>
      <PageHeader
        title="Installers"
        subtitle="Each product has an Online and an Offline edition. A release is emailed to the IT admins and holders of licenses of the same edition."
        actions={
          <Tooltip title="Refresh">
            <IconButton onClick={load} aria-label="Refresh">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
      />
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {!products && !error && <LoadingState />}

      {(products || []).map((product) => (
        <SectionCard key={product.product} title={product.name} subtitle={product.description}>
          {product.editions.map((slot, index) => (
            <Box key={slot.edition} sx={{ mt: index === 0 ? 0 : 3 }}>
              {index > 0 && <Divider sx={{ mb: 3 }} />}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1">{slot.edition}</Typography>
                  {slot.publicUpdates && <StatusChip label="Plugin auto-update" tone="info" />}
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<UploadIcon />}
                  onClick={() => setPublishing({ product: product.product, name: product.name, edition: slot.edition, publicUpdates: slot.publicUpdates })}
                >
                  Publish {slot.edition} version
                </Button>
              </Box>
              {slot.releases.length === 0 ? (
                <EmptyState title={`No ${product.name} ${slot.edition} installer yet`} />
              ) : (
                <ReleaseTable
                  releases={slot.releases}
                  busyId={busyId}
                  onEmail={(release) => (['Sent', 'Interrupted'].includes(release.emailStatus) ? setResend(release) : sendEmails(release))}
                  onDownload={download}
                />
              )}
            </Box>
          ))}
        </SectionCard>
      ))}

      <PublishDialog slot={publishing} open={Boolean(publishing)} onClose={() => setPublishing(null)} onPublished={handlePublished} />
      <ConfirmDialog
        open={Boolean(resend)}
        title="Email the IT admins again?"
        confirmLabel="Send again"
        confirmColor="primary"
        busy={busyId === resend?.id}
        onConfirm={() => sendEmails(resend, true)}
        onClose={() => setResend(null)}
      >
        {resend
          ? resend.emailStatus === 'Interrupted'
            ? `Sending ${resend.name} ${resend.edition} ${resend.version} was interrupted after ${resend.emailSent} of ${resend.emailRecipients} emails. Sending again emails every ${resend.edition} license admin, including those who already have it.`
            : `Every ${resend.edition} license admin was already emailed about ${resend.name} ${resend.edition} ${resend.version} on ${formatDate(resend.emailedAtUtc)}. They will get the same email again.`
          : ''}
      </ConfirmDialog>
    </Box>
  );
};

export default InstallersView;
